import React, { startTransition, useEffect, useState } from 'react';

import DashboardHero from './components/DashboardHero';
import PreviewTable from './components/PreviewTable';
import ReportsPanel from './components/ReportsPanel';
import UploadForm from './components/UploadForm';
import { DEFAULT_FORM_VALUES } from './constants/defaultFormValues';
import { fetchReports, getReportDownloadUrl, uploadReport } from './services/reportApi';
import './styles/app.css';
import { downloadReport } from './utils/download';

const INITIAL_FEEDBACK = {
  message: 'Upload an Excel file to generate a downloadable workbook.',
  type: 'info'
};

export default function App() {
  const [form, setForm] = useState(DEFAULT_FORM_VALUES);
  const [preview, setPreview] = useState([]);
  const [latestReport, setLatestReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [feedback, setFeedback] = useState(INITIAL_FEEDBACK);
  const [downloadingId, setDownloadingId] = useState('');

  useEffect(() => {
    void loadReports();
  }, []);

  async function loadReports() {
    setReportsLoading(true);

    try {
      const payload = await fetchReports();
      const firstReport = payload.data && payload.data[0] ? payload.data[0] : null;

      startTransition(() => {
        setReports(payload.data || []);
        setLatestReport((currentLatest) => currentLatest || firstReport);
        setPreview((currentPreview) => (currentPreview.length > 0 ? currentPreview : (firstReport && firstReport.preview ? firstReport.preview : [])));
      });
    } catch (error) {
      setFeedback({
        message: error.message || 'Could not load saved reports.',
        type: 'error'
      });
    } finally {
      setReportsLoading(false);
    }
  }

  function updateField(field, value) {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value
    }));
  }

  function updateFile(field, value) {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!form.file) {
      setFeedback({
        message: 'Choose an Excel file before generating the report.',
        type: 'error'
      });
      return;
    }

    setSubmitting(true);
    setFeedback({
      message: 'Generating Excel workbook. This can take a moment for large files.',
      type: 'info'
    });

    const formData = new FormData();
    formData.append('file', form.file);

    if (form.media) {
      formData.append('media', form.media);
    }

    formData.append('message', form.message.trim());
    formData.append('pending', String(form.pending));
    formData.append('sent', String(form.sent));
    formData.append('delivered', String(form.delivered));
    formData.append('read', String(form.read));
    formData.append('failed', String(form.failed));
    formData.append('rate', String(form.rate || 1000));

    if (form.campaignDatetime) {
      formData.append('campaignDatetime', form.campaignDatetime);
    }

    try {
      const payload = await uploadReport(formData);

      startTransition(() => {
        if (payload.meta) {
          setLatestReport(payload.meta);
          setReports((currentReports) => [
            payload.meta,
            ...currentReports.filter((report) => report.id !== payload.meta.id)
          ]);
        }

        setPreview(payload.preview || []);
      });

      setFeedback({
        message: 'Workbook created. Click Download Excel to save the .xlsx file.',
        type: 'success'
      });
    } catch (error) {
      setFeedback({
        message: error.message || 'Upload failed.',
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDownload(report) {
    setDownloadingId(report.id);

    try {
      await downloadReport(getReportDownloadUrl(report.id), report.filename || 'report.xlsx');
      setFeedback({
        message: `${report.filename || 'Report'} downloaded successfully.`,
        type: 'success'
      });
    } catch (error) {
      setFeedback({
        message: error.message || 'Download failed.',
        type: 'error'
      });
    } finally {
      setDownloadingId('');
    }
  }

  const totalRows = reports.reduce((sum, report) => sum + (Number(report.count) || 0), 0);

  return (
    <div className="app-shell">
      <div className="page-backdrop" />
      <main className="dashboard">
        <DashboardHero
          latestReport={latestReport || (reports[0] || null)}
          reportCount={reports.length}
          submitting={submitting}
          totalRows={totalRows}
        />

        <section className={`status-banner status-banner--${feedback.type}`}>
          <strong>{feedback.type === 'error' ? 'Attention:' : feedback.type === 'success' ? 'Ready:' : 'Status:'}</strong>
          <span>{feedback.message}</span>
        </section>

        <div className="dashboard-grid">
          <div className="main-stack">
            <UploadForm
              form={form}
              onFieldChange={updateField}
              onFileChange={updateFile}
              onRefresh={loadReports}
              onSubmit={handleSubmit}
              refreshing={reportsLoading}
              submitting={submitting}
            />
            <PreviewTable
              downloading={Boolean(latestReport) && downloadingId === latestReport.id}
              onDownload={handleDownload}
              preview={preview}
              report={latestReport}
            />
          </div>

          <ReportsPanel
            downloadingId={downloadingId}
            loading={reportsLoading}
            onDownload={handleDownload}
            reports={reports}
          />
        </div>
      </main>
    </div>
  );
}
